import { reactive, watchEffect } from 'vue'
import * as defaultCompiler from 'vue/compiler-sfc'
import { compileFile, File, OutputModes, SFCOptions, Store, StoreOptions, StoreState } from '@vue/repl'
import vuetifyTemplate from './templates/vuetify.js?raw'
import setupTemplate from './templates/setup.js?raw'
import mainTemplate from './templates/main.js?raw'
import routerTemplate from './templates/router.js?raw'
import appTemplate from './templates/App.vue?raw'

interface VuetifyStoreOptions extends Omit<StoreOptions, 'defaultVueRuntimeURL' | 'defaultVueServerRendererURL' | 'serializedState'> {
  defaultFiles?: Record<string, string>
  defaultVueVersion: string,
  defaultVuetifyVersion: string,
}

const MAIN_FILE = 'main.js'
const ROUTER_FILE = 'router.js'
const SETUP_FILE = 'setup.js'
const VUETIFY_FILE = 'vuetify.js'
const APP_FILE = 'App.vue'
const HIDDEN_FILES = [MAIN_FILE, ROUTER_FILE, SETUP_FILE]

const CDN_LINKS: Record<string, string> = {
  '@vue/compiler-sfc': '/dist/compiler-sfc.esm-browser.js',
  '@vue/runtime-dom': '/dist/runtime-dom.esm-browser.js',
  '@vue/server-renderer': '/dist/server-renderer.esm-browser.js',
  '@vue/devtools-api': '/lib/esm/index.js',
  'vue-router': '/dist/vue-router.esm-bundler.js',
  vuetify: '/dist/vuetify.esm.js',
  '@vuetify/nightly': '/dist/vuetify.esm.js'
}

function getCdnLink (pkg: string, version: string, path?: string) {
  return `https://unpkg.com/${pkg}@${version}${path ?? CDN_LINKS[pkg]}`
}

function getVuetifyCdnCssLink (version: string) {
  const pkg = /-\d{8}\.\d$/.test(version) ? '@vuetify/nightly' : 'vuetify'

  return getCdnLink(pkg, version, '/dist/vuetify.min.css')
}

function getVuetifyCdnJsLink (version: string) {
  const pkg = /-\d{8}\.\d$/.test(version) ? '@vuetify/nightly' : 'vuetify'

  return getCdnLink(pkg, version, '/dist/vuetify.esm.js')
}

export class VuetifyStore implements Store {
  state: StoreState
  compiler = defaultCompiler
  options?: SFCOptions
  initialShowOutput: boolean
  initialOutputMode: OutputModes

  private defaultVueRuntimeURL: string
  private defaultVueRouterRuntimeURL: string
  private defaultVueDevtoolsApiURL: string
  private defaultVueServerRendererURL: string
  private defaultVuetifyRuntimeURL: string
  private pendingCompiler: Promise<any> | null = null
  private defaultFiles: Record<string, File>

  constructor ({
    defaultFiles,
    defaultVueVersion,
    defaultVuetifyVersion,
    showOutput = false,
    outputMode = 'preview'
  }: VuetifyStoreOptions) {
    this.defaultFiles = {
      [MAIN_FILE]: new File(MAIN_FILE, mainTemplate, HIDDEN_FILES.includes(MAIN_FILE)),
      [ROUTER_FILE]: new File(ROUTER_FILE, routerTemplate, HIDDEN_FILES.includes(ROUTER_FILE)),
      [SETUP_FILE]: new File(SETUP_FILE, setupTemplate.replace('#CSS#', getVuetifyCdnCssLink(defaultVuetifyVersion)), HIDDEN_FILES.includes(SETUP_FILE)),
      [VUETIFY_FILE]: new File(VUETIFY_FILE, vuetifyTemplate),
      [APP_FILE]: new File(APP_FILE, appTemplate)
    }

    const files: StoreState['files'] = Object.assign(
      {},
      Object.fromEntries(Object.entries(this.defaultFiles).map(([filename, file]) =>
        [filename, new File(filename, file.code, file.hidden)]
      )),
      Object.fromEntries(Object.entries(defaultFiles ?? {}).map(([filename, code]) =>
        [filename, new File(filename, code, HIDDEN_FILES.includes(filename))]
      ))
    )

    this.defaultVueRuntimeURL = getCdnLink('@vue/runtime-dom', defaultVueVersion)
    this.defaultVueRouterRuntimeURL = getCdnLink('vue-router', '4')
    this.defaultVueDevtoolsApiURL = getCdnLink('@vue/devtools-api', '6')
    this.defaultVueServerRendererURL = getCdnLink('@vue/server-renderer', defaultVueVersion)
    this.defaultVuetifyRuntimeURL = getVuetifyCdnJsLink(defaultVuetifyVersion)
    this.initialShowOutput = showOutput
    this.initialOutputMode = outputMode as OutputModes

    let mainFile = MAIN_FILE
    if (!files[MAIN_FILE]) {
      mainFile = Object.keys(files)[0]
    }

    this.state = reactive({
      mainFile,
      files,
      activeFile: files[APP_FILE],
      errors: [],
      vueRuntimeURL: this.defaultVueRuntimeURL,
      vueServerRendererURL: this.defaultVueServerRendererURL,
      resetFlip: true
    })

    this.initImportMap()
  }

  // don't start compiling until the options are set
  init () {
    watchEffect(() => compileFile(this, this.state.activeFile))
    for (const file in this.state.files) {
      if (file !== APP_FILE) {
        compileFile(this, this.state.files[file])
      }
    }
  }

  setActive (filename: string) {
    this.state.activeFile = this.state.files[filename]
  }

  addFile (fileOrFilename: string | File): void {
    const file =
      typeof fileOrFilename === 'string'
        ? new File(fileOrFilename)
        : fileOrFilename
    this.state.files[file.filename] = file
    if (!file.hidden) this.setActive(file.filename)
  }

  deleteFile (filename: string) {
    if (confirm(`Are you sure you want to delete ${filename}?`)) {
      if (this.state.activeFile.filename === filename) {
        this.state.activeFile = this.state.files[this.state.mainFile]
      }
      delete this.state.files[filename]
    }
  }

  getFiles () {
    const exported: Record<string, string> = {}
    for (const filename in this.state.files) {
      const file = this.state.files[filename]
      const defaultFile = this.defaultFiles[filename]
      if (file.code !== defaultFile?.code) {
        exported[filename] = file.code
      }
    }
    return exported
  }

  private initImportMap () {
    const map = this.state.files['import-map.json']
    if (!map) {
      this.state.files['import-map.json'] = new File(
        'import-map.json',
        JSON.stringify({
          imports: {
            vue: this.defaultVueRuntimeURL,
            'vue-router': this.defaultVueRouterRuntimeURL,
            '@vue/devtools-api': this.defaultVueDevtoolsApiURL,
            vuetify: this.defaultVuetifyRuntimeURL,
            'vue/server-renderer': this.defaultVueServerRendererURL
          }
        }, null, 2)
      )
    } else {
      try {
        const json = JSON.parse(map.code)
        if (!json.imports.vuetify) {
          json.imports.vuetify = this.defaultVuetifyRuntimeURL
          map.code = JSON.stringify(json, null, 2)
        }
        if (!json.imports.vue) {
          json.imports.vue = this.defaultVueRuntimeURL
          map.code = JSON.stringify(json, null, 2)
        }
        if (!json.imports['vue-router']) {
          json.imports['vue-router'] = this.defaultVueRouterRuntimeURL
          map.code = JSON.stringify(json, null, 2)
        }
        if (!json.imports['@vue/devtools-api']) {
          json.imports['@vue/devtools-api'] = this.defaultVueDevtoolsApiURL
          map.code = JSON.stringify(json, null, 2)
        }
        if (!json.imports['vue/server-renderer']) {
          json.imports['vue/server-renderer'] = this.defaultVueServerRendererURL
          map.code = JSON.stringify(json, null, 2)
        }
      } catch (e) {}
    }
  }

  getImportMap () {
    try {
      return JSON.parse(this.state.files['import-map.json'].code)
    } catch (e) {
      this.state.errors = [
        `Syntax error in import-map.json: ${(e as Error).message}`
      ]
      return {}
    }
  }

  setImportMap (map: {
    imports: Record<string, string>
    scopes?: Record<string, Record<string, string>>
  }) {
    this.state.files['import-map.json']!.code = JSON.stringify(map, null, 2)
  }

  async setVueVersion (version: string) {
    const compilerUrl = getCdnLink('@vue/compiler-sfc', version)
    const runtimeUrl = getCdnLink('@vue/runtime-dom', version)
    const ssrUrl = getCdnLink('@vue/server-renderer', version)
    this.pendingCompiler = import(/* @vite-ignore */ compilerUrl)
    this.compiler = await this.pendingCompiler
    this.pendingCompiler = null
    this.state.vueRuntimeURL = runtimeUrl
    this.state.vueServerRendererURL = ssrUrl
    const importMap = this.getImportMap()
    const imports = importMap.imports || (importMap.imports = {})
    imports.vue = runtimeUrl
    imports['vue/server-renderer'] = ssrUrl
    this.setImportMap(importMap)
    console.info(`[@vue/repl] Now using Vue version: ${version}`)
  }

  async setVuetifyVersion (version: string) {
    const importMap = this.getImportMap()
    const imports = importMap.imports ?? (importMap.imports = {})

    imports.vuetify = getVuetifyCdnJsLink(version)

    this.setImportMap(importMap)

    const file = new File(SETUP_FILE, setupTemplate.replace('#CSS#', getVuetifyCdnCssLink(version)), true)
    this.state.files[SETUP_FILE] = file
    this.defaultFiles[SETUP_FILE] = new File(file.filename, file.code, file.hidden)
    compileFile(this, file)

    console.info(`[@vue/repl] Now using Vuetify version: ${version}`)
  }
}
